import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLangBar from "./AdminLangBar";
import { api, API_URL } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ExternalLink } from "lucide-react";

const inputCls =
  "w-full bg-transparent border-b border-[#E5DFD5] py-2.5 text-sm focus:outline-none focus:border-[#C5A880]";

const FieldLabel = ({ children }) => (
  <label className="text-[10px] tracking-[0.25em] uppercase text-[#73706B] mb-2 block">
    {children}
  </label>
);

const DEFAULTS = {
  title: "SEO & Sitemap",
  site_title: "Finca Cortesin New Developments | Luxury Golf Property in Casares, Spain",
  site_description: "Discover the most exclusive new build apartments, penthouses and villas inside Finca Cortesín, Casares.",
  site_keywords: "Finca Cortesin, new developments, Casares, luxury property Spain, frontline golf, sea view villas, Costa del Sol",
  og_image: "",
  twitter_handle: "",
  robots_extra: "",
};

export default function AdminSEOEdit() {
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState("en");
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    setPage(null);
    (async () => {
      try {
        const { data } = await api.get("/admin/pages/seo", { params: { lang } });
        setPage({ ...DEFAULTS, ...data });
      } catch (_err) {
        setPage({ key: "seo", ...DEFAULTS });
      }
    })();
  }, [lang, reloadTick]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { key: _, ...payload } = page;
      await api.put("/admin/pages/seo", payload, { params: { lang } });
      toast.success("SEO saved");
    } catch (_err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!page)
    return (
      <div className="p-10">
        <Loader2 className="w-6 h-6 animate-spin text-[#C5A880]" strokeWidth={1.5} />
      </div>
    );

  const set = (k, v) => setPage({ ...page, [k]: v });
  const sitemapUrl = `${API_URL}/api/sitemap.xml`;
  const robotsUrl = `${API_URL}/api/robots.txt`;

  return (
    <div className="p-10 lg:p-16 max-w-[900px]" data-testid="admin-seo-edit">
      <Link
        to="/admin/pages"
        className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#73706B] hover:text-[#1A1A1A] mb-8"
      >
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> All Pages
      </Link>
      <h1
        className="text-4xl lg:text-5xl font-light mb-2"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        SEO &amp; Sitemap
      </h1>
      <p className="text-sm text-[#73706B] mb-12">
        Site-wide search-engine metadata. The sitemap and robots.txt regenerate live on every request.
      </p>

      <AdminLangBar lang={lang} onChange={setLang} kind="page" docKey={"seo"} disabled={false} onTranslated={() => setReloadTick((n) => n + 1)} />

      <form onSubmit={save} className="space-y-10">
        <section className="space-y-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— Site-wide meta</h2>
          <div>
            <FieldLabel>Default page title (browser tab)</FieldLabel>
            <input
              value={page.site_title || ""}
              onChange={(e) => set("site_title", e.target.value)}
              className={inputCls}
              data-testid="seo-field-site-title"
            />
          </div>
          <div>
            <FieldLabel>Default meta description</FieldLabel>
            <textarea
              value={page.site_description || ""}
              onChange={(e) => set("site_description", e.target.value)}
              rows={3}
              className={`${inputCls} resize-none`}
              data-testid="seo-field-site-description"
            />
          </div>
          <div>
            <FieldLabel>Keywords (comma-separated)</FieldLabel>
            <textarea
              value={page.site_keywords || ""}
              onChange={(e) => set("site_keywords", e.target.value)}
              rows={2}
              className={`${inputCls} resize-none`}
              data-testid="seo-field-site-keywords"
            />
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— Social sharing</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <FieldLabel>OG / share image URL</FieldLabel>
              <input
                value={page.og_image || ""}
                onChange={(e) => set("og_image", e.target.value)}
                placeholder="https://..."
                className={inputCls}
                data-testid="seo-field-og-image"
              />
            </div>
            <div>
              <FieldLabel>Twitter handle (with @)</FieldLabel>
              <input
                value={page.twitter_handle || ""}
                onChange={(e) => set("twitter_handle", e.target.value)}
                placeholder="@enovaestates"
                className={inputCls}
                data-testid="seo-field-twitter"
              />
            </div>
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— robots.txt extras</h2>
          <div>
            <FieldLabel>Additional robots directives (optional)</FieldLabel>
            <textarea
              value={page.robots_extra || ""}
              onChange={(e) => set("robots_extra", e.target.value)}
              rows={3}
              placeholder="Crawl-delay: 5"
              className={`${inputCls} resize-none font-mono text-xs`}
              data-testid="seo-field-robots-extra"
            />
            <p className="text-xs text-[#73706B] mt-2">
              Appended after the default rules. Leave blank for defaults.
            </p>
          </div>
        </section>

        <section className="space-y-4 border border-[#E5DFD5] bg-[#FFFFFF] p-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— Live URLs</h2>
          <div className="space-y-3 text-sm">
            <a
              href={sitemapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-[#1A1A1A] hover:text-[#C5A880]"
              data-testid="seo-link-sitemap"
            >
              <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
              <code>{sitemapUrl}</code>
            </a>
            <a
              href={robotsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-[#1A1A1A] hover:text-[#C5A880]"
              data-testid="seo-link-robots"
            >
              <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
              <code>{robotsUrl}</code>
            </a>
          </div>
          <p className="text-xs text-[#73706B]">
            Both files are generated live from the database. Developments and blog posts are auto-included.
          </p>
        </section>

        <div className="flex items-center gap-4 border-t border-[#E5DFD5] pt-8">
          <button
            type="submit"
            disabled={saving}
            data-testid="seo-save"
            className="bg-[#1A1A1A] text-[#F9F6F0] px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C5A880] disabled:opacity-60 transition-colors inline-flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
            {saving ? "Saving" : "Save SEO"}
          </button>
        </div>
      </form>
    </div>
  );
}
